import { DateTime } from 'luxon';
import { decode } from 'html-entities';
import type { IncomingMessage, ServerResponse } from 'http';
import { MetaProps, PageType } from '@benzinga/seo';
import { Financials } from '@benzinga/securities-manager';
import { Ratings } from '@benzinga/calendar-manager';
import { DelayedQuote, QuoteSessionType, Schedule, TickerDetail, QuotesManager } from '@benzinga/quotes-manager';
import { NodeQueryParamsTokenType, News, getTopPostsUsingChartbeatInternal } from '@benzinga/basic-news-manager';
import type { StoryObject } from '@benzinga/advanced-news-manager';
import { simplyQueryInternalNews } from '@benzinga/internal-news-manager';
import type { LocaleType } from '@benzinga/translate';
import { LOCALES } from '@benzinga/translate/constants';
import type {
  RatingsByMonthWithCounts,
  ActiveTab,
  BaseQuoteProfile,
  BaseQuoteProfileResult,
  QuoteProfileError,
  QuoteNotFoundPageData,
} from './types';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { formatTickerToUppercase, formatTickerToURLFriendly } from '@benzinga/utils';
import { AutocompleteManager } from '@benzinga/autocomplete-manager';
import { MoversManager } from '@benzinga/movers-manager';
import { getGlobalSession } from '../session';
import { TIMEZONE } from '../../constants';
import { SafeError, SafePromise } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';
import { Content, PressContent } from '../../entities/newsEntity';

export const QuotePageTabSlugs = [
  'profile',
  'analyst-ratings',
  'guidance',
  'earnings',
  'dividend',
  'news',
  'short-interest',
  'insider-trades',
  'ideas',
];

const zone = 'America/Detroit';
const now = DateTime.now().setZone(zone);
export const DATE_CREATED = '2019-01-01T01:01:01';
export const DATE_UPDATED = now.toISO();

export const quoteHeadingFromMetaTitle = (title: string | undefined | null): string => {
  if (!title?.trim()) {
    return '';
  }
  return title.replace(/\s*\|\s*Benzinga\s*$/, '').trim();
};

const DAYS_OF_PRIORITY = 7;

export const isTab = (value: string) => {
  return QuotePageTabSlugs.includes(value);
};

export const calculateFundamentalRatings = (
  fundamentals: Financials | null,
): { financialDown: number; financialUp: number } => {
  if (
    !fundamentals?.earningReports?.[0]?.basicEps ||
    !fundamentals?.valuationRatios?.[0]?.peRatio ||
    !fundamentals?.valuationRatios?.[0]?.pbRatio ||
    !fundamentals?.operationRatios?.[0]?.roe ||
    !fundamentals?.operationRatios?.[0]?.totalDebtEquityRatio
  ) {
    return { financialDown: 0, financialUp: 0 };
  }

  let financialUp = 0;
  let financialDown = 5;

  if (fundamentals?.earningReports?.[0]?.basicEps > 0.5) {
    financialUp++;
    financialDown--;
  }

  if (fundamentals?.valuationRatios?.[0]?.peRatio < 17) {
    financialUp++;
    financialDown--;
  }

  if (fundamentals?.valuationRatios?.[0]?.pbRatio < 1) {
    financialUp++;
    financialDown--;
  }

  if (fundamentals?.operationRatios?.[0]?.roe > 0.15) {
    financialUp++;
    financialDown--;
  }

  if (fundamentals?.operationRatios?.[0]?.totalDebtEquityRatio < 2) {
    financialUp++;
    financialDown--;
  }
  return { financialDown, financialUp };
};

export const calculateTechnicalRatings = (data: {
  richQuoteData: { lastTradePrice: number; fiftyTwoWeekLow: number; fiftyTwoWeekHigh: number };
  technicals: { rsi: number };
  ratingsByMonthWithCounts: RatingsByMonthWithCounts[];
}): { technicalDown: number; technicalUp: number } => {
  if (
    !data.technicals?.rsi ||
    !data?.richQuoteData?.lastTradePrice ||
    !data?.richQuoteData?.fiftyTwoWeekLow ||
    !data?.richQuoteData?.fiftyTwoWeekHigh ||
    !data.ratingsByMonthWithCounts?.length
  ) {
    return { technicalDown: 0, technicalUp: 0 };
  }

  let technicalUp = 0;
  let technicalDown = 0;
  let totalBuyRatings = 0;
  let totalSellRatings = 0;

  data.ratingsByMonthWithCounts.forEach(month => {
    totalBuyRatings += month?.ratingBuyCount + month?.ratingStrongBuyCount;
    totalSellRatings += month?.ratingSellCount + month?.ratingStrongSellCount;
  });

  if (totalBuyRatings > totalSellRatings) {
    technicalUp++;
  } else {
    technicalDown++;
  }

  if (data.technicals?.rsi < 70) {
    technicalUp++;
  } else {
    technicalDown++;
  }

  const yearLow = data?.richQuoteData?.fiftyTwoWeekLow;
  const yearHigh = data?.richQuoteData?.fiftyTwoWeekHigh;
  const yearMidpoint = (yearHigh + yearLow) / 2;

  if (data?.richQuoteData?.lastTradePrice > yearMidpoint) {
    technicalUp++;
  } else {
    technicalDown++;
  }

  return { technicalDown, technicalUp };
};

export const calculateRatingsByMonthWithCounts = (ratings: Ratings[]): RatingsByMonthWithCounts[] => {
  const ratingsByMonth: { [key: string]: Ratings[] } = {};
  ratings?.forEach(rating => {
    const dateNow = DateTime.fromJSDate(new Date());
    const ratingDateFormatted = DateTime.fromISO(rating.date).toFormat('LLL/yyyy');

    const sixMonthsAgo = dateNow.minus({ months: 6 });
    const ratingDate = DateTime.fromISO(rating.date);
    if (ratingDate < sixMonthsAgo) return;
    if (ratingsByMonth[ratingDateFormatted]) {
      ratingsByMonth[ratingDateFormatted].push(rating);
    } else {
      ratingsByMonth[ratingDateFormatted] = [rating];
    }
  });

  const ratingsByMonthWithCounts = Object.keys(ratingsByMonth).map(key => {
    const ratings = ratingsByMonth[key];
    const ratingStrongBuyCount = ratings.filter(rating => rating.rating_current === 'Strong Buy').length;
    const ratingBuyCount = ratings.filter(rating =>
      ['Buy', 'Outperform', 'Overweight'].includes(rating.rating_current),
    ).length;
    const ratingHoldCount = ratings.filter(rating =>
      ['Market Perform', 'Equal-Weight', 'Neutral'].includes(rating.rating_current),
    ).length;
    const ratingSellCount = ratings.filter(rating =>
      ['Sell', 'Underweight', 'Underperform'].includes(rating.rating_current),
    ).length;
    const ratingStrongSellCount = ratings.filter(rating => ['Strong Sell'].includes(rating.rating_current)).length;
    const ratingCountTotal =
      ratingStrongBuyCount + ratingBuyCount + ratingHoldCount + ratingStrongSellCount + ratingSellCount;
    return {
      date: key,
      ratingBuyCount,
      ratingCountTotal,
      ratingHoldCount,
      ratingSellCount,
      ratingStrongBuyCount,
      ratingStrongSellCount,
    };
  });

  ratingsByMonthWithCounts.sort((a, b) => {
    const aDate = DateTime.fromFormat(a.date, 'LLL/yyyy');
    const bDate = DateTime.fromFormat(b.date, 'LLL/yyyy');
    return aDate > bDate ? 1 : -1;
  });

  return ratingsByMonthWithCounts;
};

export const getQuoteFeedTypeByLocale = (locale: LocaleType): string | undefined => {
  const feedsType = {
    [LOCALES.KO]: 'benzinga_wire_korea',
    [LOCALES.JA]: 'benzinga_wire_japan',
    [LOCALES.ES]: 'benzinga_wire_espanol',
    [LOCALES.IT]: 'benzinga_wire_italia',
    [LOCALES.FR]: 'benzinga_wire_france',
  };

  return feedsType[locale] || undefined;
};

export const getQuoteFeedTokenTypeByLocale = (locale: LocaleType): NodeQueryParamsTokenType | undefined => {
  const feedsType = {
    [LOCALES.KO]: 'korea',
    [LOCALES.JA]: 'japan',
    [LOCALES.ES]: 'spain',
    [LOCALES.IT]: 'italy',
    [LOCALES.FR]: 'france',
  };

  return feedsType[locale] || undefined;
};

export const prioritizeSponsoredStory = (news: StoryObject[] | News[]): void => {
  if (news[0]?.meta && !news[0]?.meta?.Reach) return;

  const reachArticleIndex = news.findIndex(item => {
    return !!item?.meta?.Reach;
  });

  const reachArticle = news[reachArticleIndex];

  if (reachArticle?.created) {
    const dateToCheck = DateTime.fromJSDate(new Date(reachArticle?.created));
    const diffInDays = dateToCheck.diffNow('days').days;

    if (Math.abs(diffInDays) <= DAYS_OF_PRIORITY) {
      news.splice(0, 0, news.splice(reachArticleIndex, 1)[0] as News);
    }
  }
};

export const injectSponsoredNodesInFeed = (
  feed: News[],
  sponsoredNodes: StoryObject[],
  prioritize?: boolean,
): News[] => {
  const nodes = [...feed, ...sponsoredNodes];

  const sortedContent = nodes.sort((a: News | StoryObject, b: News | StoryObject) => {
    const aCreated = new Date(a.created);
    const bCreated = new Date(b.created);

    const dateA = aCreated ? DateTime.fromJSDate(aCreated, { locale: 'en' }) : null;
    const dateB = bCreated ? DateTime.fromJSDate(bCreated, { locale: 'en' }) : null;

    if (dateB && dateA) {
      return dateB?.toMillis() - dateA?.toMillis();
    }

    return 0;
  });

  if (prioritize) {
    prioritizeSponsoredStory(sortedContent as News[]);
  }

  return sortedContent as News[];
};

export const getCurrentSessionType = (schedule?: Schedule): QuoteSessionType | null => {
  const activeSessions = schedule?.sessions?.filter(session => {
    const startTime = DateTime.fromMillis(session.startTime);
    const endTime = DateTime.fromMillis(session.endTime);
    const currentTime = DateTime.fromMillis(new Date().getTime());

    return currentTime > startTime && currentTime < endTime;
  });

  if (activeSessions?.length) {
    return activeSessions[0].type as QuoteSessionType;
  }

  return null;
};

export const isQueryTickerValid = (symbol: string): boolean => {
  return symbol?.length < 30;
};

export interface PageTargeting {
  BZ_PTYPE: string;
  BZ_TICKER: string;
  BZ_CHANNEL?: string;
  md_key?: 'ZKCovered';
}

export const getPageTargeting = async (symbol: string, activeTab: ActiveTab): Promise<PageTargeting> => {
  const pageTargeting: PageTargeting = { BZ_PTYPE: 'ticker', BZ_TICKER: symbol };

  switch (activeTab) {
    case 'news':
      pageTargeting.BZ_CHANNEL = 'News';
      break;
    case 'analyst-ratings':
      pageTargeting.BZ_CHANNEL = 'Ratings';
      break;
    case 'dividends':
      pageTargeting.BZ_CHANNEL = 'Dividend';
      break;
    case 'earnings':
      pageTargeting.BZ_CHANNEL = 'Earnings';
      break;
    case 'events':
      pageTargeting.BZ_CHANNEL = 'Events';
      break;
    case 'guidance':
      pageTargeting.BZ_CHANNEL = 'Guidance';
      break;
    case 'insider-trades':
      pageTargeting.BZ_CHANNEL = 'Insider Trades';
      break;
    case 'short-interest':
      pageTargeting.BZ_CHANNEL = 'Short Interest';
      break;
  }

  return pageTargeting;
};

export const isQuoteProfileError = (profile: BaseQuoteProfileResult): profile is QuoteProfileError => {
  return 'notFound' in profile && 'errorCode' in profile;
};

export interface QuotePageProps {
  activeTab: ActiveTab;
  metaProps: MetaProps;
  pageTargeting: PageTargeting;
  profile: BaseQuoteProfile;
  symbol: string;
  [key: string]: any;
  notFound?: boolean;
  errorCode?: number;
}

export const force404ForQuotePage = (symbol: string): boolean => {
  const force404Symbols = ['CV-USD'];
  return force404Symbols.includes(symbol.toUpperCase());
};

export const getQuotePageProps = async (
  ticker: string,
  activeTab: ActiveTab,
  req: IncomingMessage,
  res: ServerResponse<IncomingMessage>,
  additionalProps?: (
    profile: BaseQuoteProfile,
    metaProps: MetaProps,
    pageTargeting?: PageTargeting,
  ) => Promise<Record<string, any>>,
): Promise<QuotePageProps | QuoteNotFoundPageData | { notFound: boolean; errorCode?: number }> => {
  const host = req?.headers?.host;
  const { setLanguageByHost } = await import('@benzinga/translate');
  const translations = host ? await setLanguageByHost(host, ['common', 'quote']) : null;
  const symbol: string = formatTickerToUppercase(sanitizeHTML(ticker)) || '';

  if (force404ForQuotePage(symbol)) {
    res.statusCode = 404;
    return { notFound: true, errorCode: 404 };
  }

  const { getBaseQuoteProfile } = await import('./tab-data');
  const profile = await getBaseQuoteProfile(symbol);

  if (isQuoteProfileError(profile)) {
    res.statusCode = profile.errorCode;
    if (profile.notFound) {
      const notFoundData = await getQuoteNotFoundData(symbol, host);
      return notFoundData;
    }
    return { notFound: false, errorCode: profile.errorCode };
  }

  if (!profile) {
    res.statusCode = 500;
    return { notFound: false, errorCode: 500 };
  }

  const { getStockMetaInfo } = await import('./meta');
  const metaProps = await getStockMetaInfo(
    {
      richQuoteData: profile.richQuoteData,
      symbol: symbol,
      logoUrl: profile.logoUrl,
      tickerDetails: profile.tickerDetails,
    },
    activeTab,
    host,
    translations,
  );

  const pageTargeting = await getPageTargeting(symbol, activeTab);

  const baseProps: QuotePageProps = {
    activeTab,
    metaProps,
    pageTargeting,
    profile,
    symbol,
  };

  if (typeof additionalProps === 'function') {
    const extraProps = await additionalProps(profile, metaProps, pageTargeting);
    return { ...baseProps, ...extraProps };
  }

  return baseProps;
};

export const getQuoteNotFoundData = async (
  symbol: string,
  host?: string,
  moversLimit?: number,
): Promise<QuoteNotFoundPageData> => {
  const session = getGlobalSession();
  const normalizedHost = host?.replace('next.benzinga.com', 'www.benzinga.com');
  const canonical = `https://${normalizedHost}/quote/${formatTickerToURLFriendly(symbol)}`;
  const metaTitle = `${symbol.toUpperCase()} Page Not Found - Benzinga`;
  const generateMetaDescription = `The Symbol ${symbol.toUpperCase()} was not found. Here are some similar symbols and popular symbols to consider.`;
  const metaProps: MetaProps = {
    author: 'Benzinga',
    canonical,
    dateCreated: DATE_CREATED,
    dateUpdated: DATE_UPDATED,
    description: generateMetaDescription,
    pageType: PageType.Ticker,
    title: metaTitle,
  };

  try {
    let search = symbol;
    if (symbol.includes('/USD')) {
      search = symbol.split('/')[0];
    }

    let autocompleteSymbols = await session.getManager(AutocompleteManager).getAutocompleteListing(search);
    if (autocompleteSymbols?.ok?.result?.length === 0) {
      autocompleteSymbols = await session.getManager(AutocompleteManager).getAutocompleteListing(search.slice(0, -1));
    }
    const similarSymbols = Array.isArray(autocompleteSymbols?.ok?.result)
      ? autocompleteSymbols.ok.result.map(item => item.symbol)
      : [];
    const similarQuotesReq = await session.getManager(QuotesManager).getDelayedQuotes(similarSymbols);
    const similarQuotes = similarSymbols
      ?.map(symbol => similarQuotesReq?.ok?.[symbol] ?? null)
      .filter(quote => quote !== null)
      .filter(quote => !quote.error)
      .slice(0, 5);

    const news = await getTopPostsUsingChartbeatInternal(session, {}, { source: 'bz-quote:chartbeat-popular' });
    const movers = await session.getManager(MoversManager).getMovers({ maxResults: moversLimit ?? 5 });

    return {
      altSuggestions: {
        movers: movers?.ok ?? null,
        news: news?.ok ?? [],
        similarSymbols: similarQuotes ?? [],
      },
      metaProps,
      notFound: true,
      symbol,
    };
  } catch (error) {
    console.error('(getQuoteNotFoundData) Error:', error);
    return {
      notFound: true,
      errorCode: 503,
    };
  }
};

export const formatTickerWithPoint = (ticker: string): string => {
  if (!ticker) return '';
  return ticker.replace('-', '.').replace('/', '.');
};

export const fetchTickerDetails = async (
  symbols: string[],
): Promise<{
  quotes: Record<string, DelayedQuote> | null;
  tickerDetails: TickerDetail[];
}> => {
  try {
    const session = getGlobalSession();
    const quotesManager = session.getManager(QuotesManager);
    const tickerDetailsResponse = await quotesManager.getTickerDetails(symbols);
    const tickerDetails = tickerDetailsResponse?.ok?.result || [];
    const peers = tickerDetails?.[0]?.peers;

    const detailedTickersSymbols: string[] = Array.isArray(peers) ? peers.map(peer => peer.symbol) : [];
    const delayedQuotesSymbols: string[] = [...symbols, ...detailedTickersSymbols].slice(0, 30);

    const { ok: delayedQuotesResponse } = await quotesManager.getDelayedQuotes(delayedQuotesSymbols);

    return { quotes: delayedQuotesResponse ?? null, tickerDetails: tickerDetails };
  } catch (error) {
    return { quotes: null, tickerDetails: [] };
  }
};

const sortContentByDate = (content: News[]): News[] => {
  return Object.values(content).sort((a: News, b: News) => {
    const dateA = a.created ? DateTime.fromRFC2822(a.created, { locale: 'en' }) : null;
    const dateB = b.created ? DateTime.fromRFC2822(b.created, { locale: 'en' }) : null;

    if (dateB && dateA) {
      return dateB?.toMillis() - dateA?.toMillis();
    }

    return 0;
  });
};

export const getPressContentData = (news: News[], symbol: string): PressContent | null => {
  if (!Array.isArray(news) || news.length === 0) {
    return null;
  }

  const content: Content = {};
  const contentStock: { [key: string]: { name: string } } = {};
  const pressContent: { [key: string]: (number | string)[] } = {};

  news.forEach(news => {
    content[news.id] = news;

    news.stocks?.forEach(stock => {
      if (typeof stock === 'object' && 'name' in stock) {
        contentStock[stock.name] = { name: stock.name };
      }
    });

    news.stocks?.forEach(stock => {
      const stockName = typeof stock === 'object' && 'name' in stock ? stock.name : stock;
      const key = symbol === 'bestOf' ? 'bestOf' : stockName;

      if (!pressContent[key]) {
        pressContent[key] = [];
      }
      pressContent[key].push(news.id);
    });
  });

  const sortedContent = sortContentByDate(Object.values(content));

  return {
    content,
    contentStock,
    pressContent,
    sortedContent,
  };
};

export const fetchWiim = async (symbol: string): SafePromise<Partial<StoryObject> | null> => {
  try {
    const session = getGlobalSession();
    const ticker = formatTickerToUppercase(symbol) || symbol;
    const wiimRes = await simplyQueryInternalNews(
      session,
      {
        channels: [726669],
        primaryTicker: ticker,
        isBzProPost: true,
      },
      {
        after: DateTime.now().minus({ days: 1 }).setZone(TIMEZONE).toFormat('yyyy-MM-dd'),
        headlines: 'include',
        limit: 1,
      },
      { source: 'bz-quote-helpers:wiim' },
    );
    if (wiimRes.err) {
      return {
        err: wiimRes.err,
      };
    }
    const story = wiimRes.ok?.[0];
    if (!story) return { ok: null };
    return {
      ok: {
        url: story.url,
        title: decode(story.title ?? ''),
      },
    };
  } catch (error: unknown) {
    console.error('(Quote helper fetchWiim) Error:', error);
    return {
      err: error as SafeError,
    };
  }
};
